import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./HeaderTabs.css";

export default function HeaderTabs({
  currentView,
  onNavigate,
  authUser,
  favoritesType = "food",
  historyType = "overview",
}) {
  const shellRef = useRef(null);
  const tabsRef = useRef(null);
  const pinRafRef = useRef(0);
  const [isPinned, setIsPinned] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  const isAdmin = !!authUser?.is_admin;
  const isTrainer = !!authUser?.is_trainer;

  const v = useMemo(() => String(currentView || "").toLowerCase(), [currentView]);
  const ht = useMemo(() => String(historyType || "").toLowerCase(), [historyType]);
  const ft = useMemo(() => String(favoritesType || "").toLowerCase(), [favoritesType]);

  const has = useCallback(
    (...needles) => needles.some((n) => n && v.includes(String(n).toLowerCase())),
    [v]
  );

  const bucket = useMemo(() => {
    if (
      has("settings", "profile", "device", "devices", "weight", "message", "messages") ||
      has("admin", "trainer") ||
      (has("history") && ht === "weight")
    ) {
      return "settings";
    }

    if (has("goal", "goals", "habit", "habits", "coach_programs", "coach-programs", "coach")) {
      return "goals";
    }

    if (has("calendar")) return null;

    if (has("favorites") || has("favorite")) {
      return ft === "exercise" ? "workouts" : "meals";
    }

    if (has("history")) {
      return ht === "exercise" ? "workouts" : ht === "food" ? "meals" : null;
    }

    if (has("exercise", "workout", "workouts")) return "workouts";
    if (has("food", "meal", "nutrition")) return "meals";

    return null;
  }, [has, ht, ft]);

  const go = useCallback(
    (view, opts = {}) => {
      try {
        onNavigate(view, opts);
      } catch {
        onNavigate(view);
      }
    },
    [onNavigate]
  );

  const tabs = useMemo(() => {
    if (!bucket) return [];

    if (bucket === "workouts") {
      const isTracking = has("exercise");
      const isProgram = has("workouts");
      const isFavorites = has("favorite") && ft === "exercise";
      const isHistory = has("history") && ht === "exercise";

      return [
        { key: "exercise", label: "Workouts", onClick: () => go("exercise"), active: isTracking },
        { key: "exercise_hub", label: "Program", onClick: () => go("workouts"), active: isProgram },
        { key: "favorites_ex", label: "Favorites", onClick: () => go("favorites", { favoritesType: "exercise" }), active: isFavorites },
        { key: "history_ex", label: "History", onClick: () => go("history", { historyType: "exercise" }), active: isHistory },
      ];
    }

    if (bucket === "meals") {
      const isMeals = has("food");
      const isNutrition = has("nutrition");
      const isFavorites = has("favorite") && ft === "food";
      const isHistory = has("history") && ht === "food";

      return [
        { key: "food", label: "Meals", onClick: () => go("food"), active: isMeals },
        { key: "nutrition_hub", label: "Program", onClick: () => go("nutrition"), active: isNutrition },
        { key: "favorites_food", label: "Favorites", onClick: () => go("favorites", { favoritesType: "food" }), active: isFavorites },
        { key: "history_food", label: "History", onClick: () => go("history", { historyType: "food" }), active: isHistory },
      ];
    }

    if (bucket === "goals") {
      const isPrograms = has("coach_programs", "coach-programs") || (has("coach") && has("program"));
      const isGoals = has("goal") && !has("habit");
      const isHabits = has("habit");
      const isCoach = has("coach") && !isPrograms;

      return [
        { key: "coach_programs", label: "Program", onClick: () => go("coach_programs"), active: isPrograms },
        { key: "goals", label: "Goals", onClick: () => go("goals"), active: isGoals },
        { key: "habits", label: "Habits", onClick: () => go("habits"), active: isHabits },
        { key: "coach", label: "Coach", onClick: () => go("coach"), active: isCoach },
      ];
    }

    const isProfile = has("profile");
    const isDevices = has("device", "devices");
    const isMessages = has("message", "messages");
    const isHealth = has("weight") || (has("history") && ht === "weight");
    const isTrainerView = has("trainer");
    const isAdminView = has("admin");

    return [
      { key: "profile", label: "Profile", onClick: () => go("profile"), active: isProfile },
      { key: "devices", label: "Devices", onClick: () => go("devices"), active: isDevices },
      { key: "messages", label: "Messages", onClick: () => go("messages"), active: isMessages },
      { key: "weight", label: "Health", onClick: () => go("history", { historyType: "weight" }), active: isHealth },
      ...(isTrainer ? [{ key: "trainer", label: "Trainer", onClick: () => go("trainer"), active: isTrainerView }] : []),
      ...(isAdmin ? [{ key: "admin", label: "Admin", onClick: () => go("admin"), active: isAdminView }] : []),
    ];
  }, [bucket, ft, go, has, ht, isAdmin, isTrainer]);

  const updatePinned = useCallback(() => {
    const shellEl = shellRef.current;
    const tabsEl = tabsRef.current;
    if (!shellEl || !tabsEl || typeof window === "undefined") return;

    const isMobile = window.matchMedia("(max-width: 809px)").matches;
    if (!isMobile) {
      setIsPinned(false);
      setPlaceholderHeight(0);
      return;
    }

    const shellTop = shellEl.getBoundingClientRect().top;
    const tabsHeight = Math.ceil(tabsEl.getBoundingClientRect().height || tabsEl.offsetHeight || 0);

    if (shellTop <= 0) {
      setIsPinned(true);
      setPlaceholderHeight(tabsHeight);
      return;
    }

    setIsPinned(false);
    setPlaceholderHeight(0);
  }, []);

  useEffect(() => {
    if (!bucket || typeof window === "undefined") {
      setIsPinned(false);
      setPlaceholderHeight(0);
      return undefined;
    }

    const queuePinnedUpdate = () => {
      if (pinRafRef.current) return;
      pinRafRef.current = window.requestAnimationFrame(() => {
        pinRafRef.current = 0;
        updatePinned();
      });
    };

    queuePinnedUpdate();
    window.addEventListener("scroll", queuePinnedUpdate, { passive: true });
    window.addEventListener("resize", queuePinnedUpdate);

    return () => {
      window.removeEventListener("scroll", queuePinnedUpdate);
      window.removeEventListener("resize", queuePinnedUpdate);
      if (pinRafRef.current) {
        window.cancelAnimationFrame(pinRafRef.current);
        pinRafRef.current = 0;
      }
    };
  }, [bucket, tabs.length, updatePinned]);

  if (!bucket) return null;

  const gridClass = tabs.length >= 6 ? "cols-3" : "cols-4";

  return (
    <div ref={shellRef} className={`htabs-shell ${isPinned ? "is-pinned" : ""}`}>
      <div className="htabs-spacer" aria-hidden="true" style={{ height: isPinned ? `${placeholderHeight}px` : 0 }} />
      <nav ref={tabsRef} className={`htabs htabs-static ${gridClass} ${isPinned ? "is-pinned" : ""}`} aria-label="Section tabs">
        <div className="htabs-grid" role="tablist" aria-label="Section tabs list">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`htab ${t.active ? "active" : ""}`}
              onClick={t.onClick}
              role="tab"
              aria-selected={t.active}
            >
              <span className="htab-label">{t.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
